import { apiClient } from '../lib/apiClient';

/**
 * Social layer: the public journal feed, comments and likes on journal
 * entries, and visibility / likes / comments / share links of gallery images.
 * Backend: src/routes/journalPublicFeed.ts, journalComments.ts, journalLikes.ts,
 * imageDetail.ts, imageVisibility.ts, imageLikes.ts, imageComments.ts, imageShare.ts.
 */

/** A public journal entry as the Community feed shows it. Never carries a name or e-mail address. */
export interface CommunityEntry {
  id: string;
  aquariumId: string;
  aquariumName: string;
  /** Always null for now — no public display names exist; the UI shows "community member". */
  authorDisplayName: string | null;
  title: string;
  /** Rich-text HTML; render as plain text only (see toPlainParagraphs). */
  content: string;
  tags: string[];
  isOwn: boolean;
  visibility: 'public' | 'private';
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  commentCount: number;
  userHasLiked: boolean;
  /**
   * The entry's PUBLIC attached photos (never private ones), newest first, at
   * most 4, signed URLs of the small variant. Same shape as the journal list's
   * coverImage (rendered by the shared EntryPhotoPreview). Empty when none.
   */
  attachedImages?: CommunityFeedImage[];
  /** Number of public attached photos (may exceed attachedImages.length). */
  attachedImageCount?: number;
}

/** A public photo attached to a feed entry (GET /api/v1/journal-feed). */
export interface CommunityFeedImage {
  id: string;
  thumbnailUrl: string | null;
  displayUrl: string | null;
  /** Always null — image notes are never published. */
  note: null;
  width: number | null;
  height: number | null;
}

/**
 * Server-side feed filters (query params of GET /api/v1/journal-feed). There
 * is deliberately no person (name / e-mail) filter: the feed never exposes
 * user identities — the aquarium name and `mine` are the equivalent.
 */
export interface CommunityFeedFilters {
  /** ISO 8601 date-time (or YYYY-MM-DD): entries created at/after. */
  from?: string;
  /** ISO 8601 date-time (or YYYY-MM-DD, whole day): entries created at/before. */
  to?: string;
  aquariumId?: string;
  /** Case-insensitive part of the aquarium name. */
  aquariumName?: string;
  tag?: string;
  /** Only the reader's own entries. */
  mine?: boolean;
  /** Free text in title and text. */
  q?: string;
  sort?: 'newest' | 'oldest';
}

export interface CommunityFeedPage {
  entries: CommunityEntry[];
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

/** A comment as both comment APIs return it, normalised. Plain text. */
export interface SocialComment {
  id: string;
  parentId: string | null;
  body: string;
  createdAt: string;
  isOwn: boolean;
  canDelete: boolean;
  /** Written by the owner of the entry / image. */
  isEntryAuthor: boolean;
  /** Emptied by its author's deletion (replies of others are kept). */
  isDeleted: boolean;
  replies: SocialComment[];
}

export interface LikeState {
  liked: boolean;
  count: number;
}

export async function getCommunityFeed(
  page: number,
  limit: number,
  filters: CommunityFeedFilters = {},
): Promise<CommunityFeedPage> {
  const params: Record<string, string | number> = { page, limit };
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.aquariumId) params.aquariumId = filters.aquariumId;
  if (filters.aquariumName) params.aquariumName = filters.aquariumName;
  if (filters.tag) params.tag = filters.tag;
  if (filters.mine) params.mine = 'true';
  if (filters.q) params.q = filters.q;
  if (filters.sort && filters.sort !== 'newest') params.sort = filters.sort;
  const res = await apiClient.get<{ data: CommunityFeedPage }>('/api/v1/journal-feed', { params });
  return res.data;
}

export async function getCommunityEntry(entryId: string): Promise<CommunityEntry> {
  const res = await apiClient.get<{ data: CommunityEntry }>(`/api/v1/journal-feed/${encodeURIComponent(entryId)}`);
  return res.data;
}

interface RawJournalComment {
  id: string;
  parentId: string | null;
  body: string;
  createdAt: string;
  isOwn?: boolean;
  canDelete?: boolean;
  isEntryAuthor?: boolean;
  isDeleted?: boolean;
  replies?: RawJournalComment[];
}

function normaliseJournalComment(raw: RawJournalComment): SocialComment {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    body: raw.body ?? '',
    createdAt: raw.createdAt,
    isOwn: raw.isOwn === true,
    canDelete: raw.canDelete === true,
    isEntryAuthor: raw.isEntryAuthor === true,
    isDeleted: raw.isDeleted === true,
    replies: (raw.replies ?? []).map(normaliseJournalComment),
  };
}

export async function listEntryComments(entryId: string): Promise<SocialComment[]> {
  const res = await apiClient.get<{ data: RawJournalComment[] }>(
    `/api/v1/journal-entries/${encodeURIComponent(entryId)}/comments`,
  );
  return res.data.map(normaliseJournalComment);
}

export async function createEntryComment(entryId: string, body: string): Promise<SocialComment> {
  const res = await apiClient.post<{ data: RawJournalComment }>(
    `/api/v1/journal-entries/${encodeURIComponent(entryId)}/comments`,
    { body },
  );
  return normaliseJournalComment(res.data);
}

export async function deleteEntryComment(entryId: string, commentId: string): Promise<void> {
  await apiClient.delete(
    `/api/v1/journal-entries/${encodeURIComponent(entryId)}/comments/${encodeURIComponent(commentId)}`,
  );
}

export async function setEntryLike(entryId: string, liked: boolean): Promise<LikeState> {
  const body = { likeable_type: 'entry', likeable_id: entryId };
  const res = liked
    ? await apiClient.post<{ data: { liked: boolean; like_count: number } }>('/api/v1/journal-likes', body)
    : await apiClient.delete<{ data: { liked: boolean; like_count: number } }>('/api/v1/journal-likes', { body });
  return { liked: res.data.liked, count: res.data.like_count };
}

// ─── Images ──────────────────────────────────────────────────────────────────

export interface ImageDetail {
  id: string;
  /** Only for the owner. */
  aquariumId: string | null;
  aquariumName: string;
  displayUrl: string | null;
  thumbnailUrl: string | null;
  photoTakenAt: string;
  visibility: 'public' | 'private';
  isOwner: boolean;
  /** Only for the owner. */
  originalFilename: string | null;
}

export async function getImageDetail(imageId: string): Promise<ImageDetail> {
  const res = await apiClient.get<{ data: ImageDetail }>(`/api/v1/images/${encodeURIComponent(imageId)}`);
  return res.data;
}

export async function setImageVisibility(imageId: string, visibility: 'public' | 'private'): Promise<void> {
  await apiClient.put(`/api/v1/images/${encodeURIComponent(imageId)}/visibility`, { visibility });
}

export async function getImageShareUrl(imageId: string): Promise<string> {
  const res = await apiClient.get<{ data: { share_url: string } }>(
    `/api/v1/images/${encodeURIComponent(imageId)}/share`,
  );
  return res.data.share_url;
}

interface RawImageLikes {
  count: number;
  user_liked: boolean;
}

export async function getImageLikes(imageId: string): Promise<LikeState> {
  const res = await apiClient.get<{ data: RawImageLikes }>(`/api/v1/images/${encodeURIComponent(imageId)}/likes`);
  return { liked: res.data.user_liked, count: res.data.count };
}

export async function setImageLike(imageId: string, liked: boolean): Promise<LikeState> {
  const path = `/api/v1/images/${encodeURIComponent(imageId)}/likes`;
  const res = liked
    ? await apiClient.post<{ data: RawImageLikes }>(path)
    : await apiClient.delete<{ data: RawImageLikes }>(path);
  return { liked: res.data.user_liked, count: res.data.count };
}

interface RawImageComment {
  id: string;
  parentId: string | null;
  body: string;
  createdAt: string;
  isOwn?: boolean;
  canDelete?: boolean;
  isDeleted?: boolean;
}

function normaliseImageComment(raw: RawImageComment): SocialComment {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    body: raw.body ?? '',
    createdAt: raw.createdAt,
    isOwn: raw.isOwn === true,
    canDelete: raw.canDelete === true,
    isEntryAuthor: false,
    isDeleted: raw.isDeleted === true,
    replies: [],
  };
}

export async function listImageComments(imageId: string): Promise<SocialComment[]> {
  const res = await apiClient.get<{ data: { comments: RawImageComment[] } }>(
    `/api/v1/images/${encodeURIComponent(imageId)}/comments`,
  );
  return res.data.comments.map(normaliseImageComment);
}

export async function createImageComment(imageId: string, body: string): Promise<SocialComment> {
  const res = await apiClient.post<{ data: { comment: RawImageComment } }>(
    `/api/v1/images/${encodeURIComponent(imageId)}/comments`,
    { body },
  );
  return normaliseImageComment(res.data.comment);
}

export async function deleteImageComment(imageId: string, commentId: string): Promise<void> {
  await apiClient.delete(
    `/api/v1/images/${encodeURIComponent(imageId)}/comments/${encodeURIComponent(commentId)}`,
  );
}

/**
 * Rich-text HTML (journal entries) → plain paragraphs. Rendered as React text
 * nodes (escaped), never as HTML.
 */
export function toPlainParagraphs(html: string | null | undefined): string[] {
  return (html ?? '')
    .replace(/<\/(p|div|h[1-6]|li)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
